import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  adminMe,
  fetchCategories,
  fetchBrands,
  fetchProducts,
  adminUploadCatalog,
  adminDeleteCatalog,
  adminListBOQs,
  adminDeleteBOQ,
  adminCreateBrand,
  adminUpdateBrand,
  adminDeleteBrand,
  adminUploadBrandLogo,
  adminDeleteBrandLogo,
  fetchBrandSeries,
  adminCreateSeries,
  adminUpdateSeries,
  adminDeleteSeries,
  adminReorderSeries,
  adminUploadSeriesCatalog,
  adminDeleteSeriesCatalog,
  seriesCatalogUrl,
  seriesCatalogViewUrl,
  adminCreateProduct,
  adminUpdateProduct,
  adminDeleteProduct,
  adminUploadProductImage,
  adminUploadProductImages,
  adminDeleteProductImage,
  productImageSrc,
  adminUploadCategoryImage,
  adminResetCategoryImage,
  fetchShowroomPhotos,
  adminUploadShowroomPhotos,
  adminDeleteShowroomPhoto,
  catalogDownloadUrl,
  catalogViewUrl,
  API,
} from "../lib/api";
import { LogOut, Upload, Trash2, FileText, Mail, Phone, Download, Eye, Inbox, FolderOpen, Plus, Pencil, X, Layers, Package, ImagePlus, Image as ImageIcon, Building2, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import ImageCropperModal from "../components/ImageCropperModal";

export default function AdminPage() {
  const nav = useNavigate();
  const [tab, setTab] = useState("catalogs");
  const [categories, setCategories] = useState([]);
  const [brandsByCat, setBrandsByCat] = useState({});
  const [activeCat, setActiveCat] = useState(null);
  const [boqs, setBoqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | brand object
  const [seriesBrand, setSeriesBrand] = useState(null); // brand whose series panel is open
  const [productsBrand, setProductsBrand] = useState(null); // brand whose product panel is open
  const [uploadProgress, setUploadProgress] = useState({}); // { brandId: 0..100 }

  useEffect(() => {
    adminMe()
      .then(async () => {
        const cats = await fetchCategories();
        setCategories(cats);
        setActiveCat(cats[0]?.id);
        const map = {};
        await Promise.all(
          cats.map(async (c) => {
            map[c.id] = await fetchBrands(c.id);
          })
        );
        setBrandsByCat(map);
        setLoading(false);
      })
      .catch(() => {
        localStorage.removeItem("ve_admin_token");
        nav("/admin/login");
      });
  }, [nav]);

  useEffect(() => {
    if (tab === "boqs") {
      adminListBOQs().then(setBoqs).catch(() => setBoqs([]));
    }
  }, [tab]);

  const logout = () => {
    localStorage.removeItem("ve_admin_token");
    nav("/admin/login");
  };

  const reloadBrands = async (catId) => {
    const list = await fetchBrands(catId);
    setBrandsByCat((m) => ({ ...m, [catId]: list }));
  };

  const handleUpload = async (brand, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Only PDF files allowed.");
      return;
    }
    setUploadProgress((p) => ({ ...p, [brand.id]: 0 }));
    try {
      await adminUploadCatalog(brand.id, file, (pct) => {
        setUploadProgress((p) => ({ ...p, [brand.id]: pct }));
      });
      toast.success(`Catalog uploaded for ${brand.name}`);
      await reloadBrands(brand.category_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed.");
    } finally {
      setUploadProgress((p) => {
        const n = { ...p };
        delete n[brand.id];
        return n;
      });
    }
  };

  const handleRemoveCatalog = async (brand) => {
    try {
      await adminDeleteCatalog(brand.id);
      toast.success("Catalog removed.");
      await reloadBrands(brand.category_id);
    } catch (e) {
      toast.error("Remove failed.");
    }
  };

  const downloadBOQFile = async (boq) => {
    try {
      const token = localStorage.getItem("ve_admin_token");
      const r = await fetch(`${API}/files/boq/${boq.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error("Download failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = boq.file_name || `boq-${boq.id}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download.");
    }
  };

  const removeBOQ = async (id) => {
    if (!window.confirm("Delete this BOQ submission?")) return;
    await adminDeleteBOQ(id);
    setBoqs((arr) => arr.filter((b) => b.id !== id));
    toast.success("Deleted.");
  };

  const saveBrand = async (payload) => {
    try {
      if (editing === "new") {
        await adminCreateBrand(payload);
        toast.success("Brand added.");
        await reloadBrands(payload.category_id);
        setActiveCat(payload.category_id);
      } else {
        await adminUpdateBrand(editing.id, payload);
        toast.success("Brand updated.");
        // reload both old and new category in case it changed
        await reloadBrands(editing.category_id);
        if (payload.category_id && payload.category_id !== editing.category_id) {
          await reloadBrands(payload.category_id);
        }
      }
      setEditing(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    }
  };

  const deleteBrand = async (brand) => {
    if (!window.confirm(`Delete "${brand.name}" and all its products?`)) return;
    try {
      await adminDeleteBrand(brand.id);
      toast.success("Brand deleted.");
      await reloadBrands(brand.category_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed.");
    }
  };

  if (loading) {
    return (
      <div className="text-center py-32 text-slate-400">Loading dashboard…</div>
    );
  }

  return (
    <div className="bg-slate-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-8 gap-3">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.25em] text-amber-600 font-bold">
              Admin
            </p>
            <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold text-slate-900 leading-tight">
              Veer Electrical Dashboard
            </h1>
          </div>
          <button
            onClick={logout}
            className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-white border border-slate-200 rounded-full text-sm hover:bg-slate-100 flex-shrink-0"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Logout</span>
          </button>
        </div>

        <div className="flex gap-2 mb-8 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 pb-1">
          <button
            onClick={() => setTab("catalogs")}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium flex-shrink-0 whitespace-nowrap ${
              tab === "catalogs"
                ? "bg-slate-900 text-white"
                : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"
            }`}
            data-testid="admin-tab-catalogs"
          >
            <FolderOpen className="w-4 h-4" /> Brand Catalogs
          </button>
          <button
            onClick={() => setTab("boqs")}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium flex-shrink-0 whitespace-nowrap ${
              tab === "boqs"
                ? "bg-slate-900 text-white"
                : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"
            }`}
            data-testid="admin-tab-boqs"
          >
            <Inbox className="w-4 h-4" /> BOQ Inbox
          </button>
          <button
            onClick={() => setTab("category-images")}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium flex-shrink-0 whitespace-nowrap ${
              tab === "category-images"
                ? "bg-slate-900 text-white"
                : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"
            }`}
            data-testid="admin-tab-images"
          >
            <ImageIcon className="w-4 h-4" /> Category Images
          </button>
          <button
            onClick={() => setTab("showroom")}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium flex-shrink-0 whitespace-nowrap ${
              tab === "showroom"
                ? "bg-slate-900 text-white"
                : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"
            }`}
            data-testid="admin-tab-showroom"
          >
            <Building2 className="w-4 h-4" /> Showroom Photos
          </button>
        </div>

        {tab === "catalogs" && (
          <div>
            <div className="flex flex-wrap gap-2 mb-6 items-center justify-between">
              <div className="flex flex-wrap gap-2">
                {categories.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setActiveCat(c.id)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium ${
                      activeCat === c.id
                        ? "bg-amber-500 text-slate-900"
                        : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"
                    }`}
                    data-testid={`admin-cat-${c.id}`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setEditing("new")}
                className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-full text-sm hover:bg-slate-800"
                data-testid="admin-add-brand"
              >
                <Plus className="w-4 h-4" /> Add Brand
              </button>
            </div>

            {/* Desktop: table view */}
            <div className="hidden md:block bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-6 py-3 text-left font-semibold">Brand</th>
                    <th className="px-6 py-3 text-left font-semibold">Catalog</th>
                    <th className="px-6 py-3 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(brandsByCat[activeCat] || []).map((b) => (
                    <tr key={b.id} data-testid={`admin-brand-row-${b.id}`}>
                      <td className="px-6 py-4 font-medium text-slate-900">
                        {b.name}
                      </td>
                      <td className="px-6 py-4 text-slate-500">
                        {b.catalog_filename ? (
                          <span className="inline-flex items-center gap-2">
                            <FileText className="w-4 h-4 text-amber-600" />
                            {b.catalog_filename}
                          </span>
                        ) : (
                          <span className="text-slate-400 italic">No catalog uploaded</span>
                        )}
                        {typeof uploadProgress[b.id] === "number" && (
                          <div className="mt-2 max-w-xs">
                            <div className="flex items-center justify-between text-[11px] text-slate-500 mb-0.5">
                              <span>Uploading…</span>
                              <span className="font-mono">{uploadProgress[b.id]}%</span>
                            </div>
                            <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-amber-500 transition-all duration-200"
                                style={{ width: `${uploadProgress[b.id]}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="inline-flex items-center gap-2 flex-wrap justify-end">
                          {b.catalog_path && (
                            <>
                              <a
                                href={catalogViewUrl(b.id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-full bg-slate-100 hover:bg-slate-200"
                                title="Open PDF in new tab"
                              >
                                <Eye className="w-3 h-3" /> View
                              </a>
                              <a
                                href={catalogDownloadUrl(b.id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-full bg-slate-100 hover:bg-slate-200"
                                title="Download PDF"
                              >
                                <Download className="w-3 h-3" /> Download
                              </a>
                              <button
                                onClick={() => handleRemoveCatalog(b)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-full bg-red-50 text-red-600 hover:bg-red-100"
                                data-testid={`admin-remove-catalog-${b.id}`}
                              >
                                <Trash2 className="w-3 h-3" /> Remove
                              </button>
                            </>
                          )}
                          <label
                            className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-full bg-amber-500 text-slate-900 hover:bg-amber-400 cursor-pointer ${typeof uploadProgress[b.id] === "number" ? "opacity-60 pointer-events-none" : ""}`}
                            data-testid={`admin-upload-${b.id}`}
                          >
                            <Upload className="w-3 h-3" />
                            {typeof uploadProgress[b.id] === "number"
                              ? `Uploading ${uploadProgress[b.id]}%`
                              : b.catalog_path ? "Replace" : "Upload"}
                            <input
                              type="file"
                              accept=".pdf"
                              className="hidden"
                              onChange={(e) =>
                                handleUpload(b, e.target.files?.[0])
                              }
                            />
                          </label>
                          <button
                            onClick={() => setSeriesBrand(b)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-full bg-slate-900 text-white hover:bg-amber-500 hover:text-slate-900"
                            data-testid={`admin-series-${b.id}`}
                            title="Manage product series / ranges"
                          >
                            <Layers className="w-3 h-3" /> Series
                          </button>
                          <button
                            onClick={() => setProductsBrand(b)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-full bg-slate-900 text-white hover:bg-amber-500 hover:text-slate-900"
                            data-testid={`admin-products-${b.id}`}
                            title="View all products of this brand"
                          >
                            <Package className="w-3 h-3" /> All products
                          </button>
                          <button
                            onClick={() => setEditing(b)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200"
                            data-testid={`admin-edit-brand-${b.id}`}
                            title="Edit brand"
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => deleteBrand(b)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-full bg-red-50 text-red-600 hover:bg-red-100"
                            data-testid={`admin-delete-brand-${b.id}`}
                            title="Delete brand"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile: card view */}
            <div className="md:hidden space-y-3">
              {(brandsByCat[activeCat] || []).map((b) => (
                <div
                  key={b.id}
                  className="bg-white rounded-2xl border border-slate-200 p-4"
                  data-testid={`admin-brand-card-${b.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-heading font-semibold text-slate-900 truncate">
                        {b.name}
                      </p>
                      <p className="text-xs text-slate-500 mt-1 truncate">
                        {b.catalog_filename ? (
                          <span className="inline-flex items-center gap-1">
                            <FileText className="w-3 h-3 text-amber-600 flex-shrink-0" />
                            <span className="truncate">{b.catalog_filename}</span>
                          </span>
                        ) : (
                          <span className="italic text-slate-400">No catalog uploaded</span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => setEditing(b)}
                        className="p-2 rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200"
                        title="Edit brand"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => deleteBrand(b)}
                        className="p-2 rounded-full bg-red-50 text-red-600 hover:bg-red-100"
                        title="Delete brand"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setSeriesBrand(b)}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs rounded-full bg-slate-900 text-white"
                    >
                      <Layers className="w-3.5 h-3.5" /> Series
                    </button>
                    <button
                      onClick={() => setProductsBrand(b)}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs rounded-full bg-slate-900 text-white"
                    >
                      <Package className="w-3.5 h-3.5" /> All products
                    </button>
                  </div>

                  {typeof uploadProgress[b.id] === "number" && (
                    <div className="mt-2 mb-1">
                      <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
                        <span>Uploading PDF…</span>
                        <span className="font-mono">{uploadProgress[b.id]}%</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-amber-500 transition-all duration-200"
                          style={{ width: `${uploadProgress[b.id]}%` }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <label className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs rounded-full bg-amber-500 text-slate-900 cursor-pointer ${typeof uploadProgress[b.id] === "number" ? "opacity-60 pointer-events-none" : ""}`}>
                      <Upload className="w-3.5 h-3.5" />
                      {typeof uploadProgress[b.id] === "number"
                        ? `${uploadProgress[b.id]}%`
                        : b.catalog_path ? "Replace catalog" : "Upload catalog"}
                      <input
                        type="file"
                        accept=".pdf"
                        className="hidden"
                        onChange={(e) => handleUpload(b, e.target.files?.[0])}
                      />
                    </label>
                    {b.catalog_path ? (
                      <a
                        href={catalogViewUrl(b.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs rounded-full bg-slate-100 text-slate-700"
                      >
                        <Eye className="w-3.5 h-3.5" /> View PDF
                      </a>
                    ) : (
                      <span className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs rounded-full bg-slate-50 text-slate-400 italic">
                        No PDF yet
                      </span>
                    )}
                  </div>

                  {b.catalog_path && (
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <a
                        href={catalogDownloadUrl(b.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs rounded-full bg-slate-100 text-slate-700"
                      >
                        <Download className="w-3.5 h-3.5" /> Download
                      </a>
                      <button
                        onClick={() => handleRemoveCatalog(b)}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs rounded-full bg-red-50 text-red-600"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Remove PDF
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "boqs" && (
          <div>
            {boqs.length === 0 ? (
              <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-16 text-center">
                <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No BOQ submissions yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {boqs.map((b) => (
                  <article
                    key={b.id}
                    className="bg-white rounded-2xl border border-slate-200 p-6"
                    data-testid={`boq-card-${b.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-heading text-lg font-semibold text-slate-900">
                          {b.name}
                        </h3>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-500">
                          <a href={`mailto:${b.email}`} className="inline-flex items-center gap-1 hover:text-amber-600">
                            <Mail className="w-3 h-3" /> {b.email}
                          </a>
                          <a href={`tel:${b.phone}`} className="inline-flex items-center gap-1 hover:text-amber-600">
                            <Phone className="w-3 h-3" /> {b.phone}
                          </a>
                        </div>
                      </div>
                      <button
                        onClick={() => removeBOQ(b.id)}
                        className="text-slate-300 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {b.message && (
                      <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                        {b.message}
                      </p>
                    )}
                    <div className="mt-4 flex items-center justify-between text-xs text-slate-400">
                      <span>{new Date(b.created_at).toLocaleString()}</span>
                      {b.file_path ? (
                        <button
                          onClick={() => downloadBOQFile(b)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-900 text-white hover:bg-amber-500 hover:text-slate-900"
                          data-testid={`boq-download-${b.id}`}
                        >
                          <Download className="w-3 h-3" />
                          {b.file_name || "Download"}
                        </button>
                      ) : (
                        <span className="italic">No attachment</span>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "category-images" && (
          <CategoryImagesPanel categories={categories} onChange={async () => {
            const cats = await fetchCategories();
            setCategories(cats);
          }} />
        )}

        {tab === "showroom" && <ShowroomPhotosPanel />}
      </div>

      {editing && (
        <BrandEditor
          initial={editing === "new" ? null : editing}
          categories={categories}
          defaultCategoryId={activeCat}
          onCancel={() => setEditing(null)}
          onSave={saveBrand}
          onLogoChanged={async () => {
            if (editing === "new" || !editing?.id) return;
            await reloadBrands(editing.category_id);
            // refresh the editing object so the new logo preview appears
            const fresh = await fetchBrands(editing.category_id);
            const updated = fresh.find((b) => b.id === editing.id);
            if (updated) setEditing(updated);
          }}
        />
      )}

      {seriesBrand && (
        <SeriesManager
          brand={seriesBrand}
          onClose={() => setSeriesBrand(null)}
        />
      )}

      {productsBrand && (
        <ProductManager
          brand={productsBrand}
          onClose={() => setProductsBrand(null)}
        />
      )}
    </div>
  );
}

function BrandEditor({ initial, categories, defaultCategoryId, onCancel, onSave, onLogoChanged }) {
  const [name, setName] = useState(initial?.name || "");
  const [categoryId, setCategoryId] = useState(
    initial?.category_id || defaultCategoryId || categories[0]?.id
  );
  const [tagline, setTagline] = useState(initial?.tagline || "");
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoToCrop, setLogoToCrop] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    await onSave({ name: name.trim(), category_id: categoryId, tagline: tagline.trim() });
    setSaving(false);
  };

  const uploadLogo = async (file) => {
    if (!file || !initial?.id) return;
    setLogoUploading(true);
    try {
      await adminUploadBrandLogo(initial.id, file);
      toast.success("Logo uploaded.");
      onLogoChanged && (await onLogoChanged());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Logo upload failed.");
    } finally {
      setLogoUploading(false);
    }
  };

  const removeLogo = async () => {
    if (!initial?.id) return;
    if (!window.confirm("Remove the brand logo?")) return;
    try {
      await adminDeleteBrandLogo(initial.id);
      toast.success("Logo removed.");
      onLogoChanged && (await onLogoChanged());
    } catch {
      toast.error("Failed to remove logo.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4 overflow-y-auto">
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 my-8"
        data-testid="brand-editor"
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading text-xl font-semibold text-slate-900">
            {initial ? "Edit Brand" : "Add Brand"}
          </h3>
          <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-900">
            <X className="w-5 h-5" />
          </button>
        </div>

        <label className="block text-xs uppercase tracking-[0.18em] text-slate-500 font-bold mb-2">
          Brand name
        </label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          placeholder="e.g. Schneider"
          className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-amber-500 outline-none mb-4"
          data-testid="brand-name-input"
          autoFocus
        />

        <label className="block text-xs uppercase tracking-[0.18em] text-slate-500 font-bold mb-2">
          Category
        </label>
        <select
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-amber-500 outline-none mb-4 bg-white"
          data-testid="brand-category-input"
        >
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        <label className="block text-xs uppercase tracking-[0.18em] text-slate-500 font-bold mb-2">
          Tagline <span className="text-slate-300">(optional)</span>
        </label>
        <input
          value={tagline}
          onChange={(e) => setTagline(e.target.value)}
          placeholder="Short descriptor shown on brand card"
          className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-amber-500 outline-none mb-4"
          data-testid="brand-tagline-input"
        />

        {initial?.id && (
          <>
            <label className="block text-xs uppercase tracking-[0.18em] text-slate-500 font-bold mb-2">
              Brand logo <span className="text-slate-300">(PNG / SVG with transparent bg works best)</span>
            </label>
            <div className="flex items-center gap-3 mb-6">
              <div className="aspect-[2/1] w-24 rounded-xl bg-slate-50 border border-slate-200 grid place-items-center overflow-hidden flex-shrink-0 p-2">
                {initial.logo_url ? (
                  <img
                    src={productImageSrc(initial.logo_url)}
                    alt=""
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  <ImageIcon className="w-5 h-5 text-slate-300" />
                )}
              </div>
              <div className="flex-1 flex flex-wrap gap-2">
                <label className={`inline-flex items-center gap-1 px-3 py-1.5 bg-slate-900 text-white rounded-full text-xs font-medium cursor-pointer hover:bg-amber-500 hover:text-slate-900 ${logoUploading ? "opacity-60 pointer-events-none" : ""}`}>
                  <Upload className="w-3 h-3" />
                  {logoUploading ? "Uploading…" : initial.logo_url ? "Replace logo" : "Upload logo"}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      if (f) setLogoToCrop(f);
                    }}
                    className="hidden"
                  />
                </label>
                {initial.logo_url && (
                  <button
                    type="button"
                    onClick={removeLogo}
                    className="px-3 py-1.5 bg-red-50 text-red-600 rounded-full text-xs font-medium hover:bg-red-100"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          </>
        )}

        {logoToCrop && (
          <ImageCropperModal
            file={logoToCrop}
            aspect={2}
            presetLabel="2:1 · Brand logo"
            onCancel={() => setLogoToCrop(null)}
            onConfirm={async (cropped) => {
              setLogoToCrop(null);
              await uploadLogo(cropped);
            }}
          />
        )}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm rounded-full bg-slate-100 hover:bg-slate-200"
          >
            Close
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-5 py-2 text-sm rounded-full bg-slate-900 text-white hover:bg-amber-500 hover:text-slate-900 disabled:opacity-50"
            data-testid="brand-save"
          >
            {saving ? "Saving…" : initial ? "Save changes" : "Add brand"}
          </button>
        </div>
      </form>
    </div>
  );
}

function SeriesManager({ brand, onClose }) {
  const [list, setList] = useState([]);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [loading, setLoading] = useState(true);
  const [seriesUploadProgress, setSeriesUploadProgress] = useState({});
  const [productsForSeries, setProductsForSeries] = useState(null); // series object whose products panel is open

  const load = async () => {
    try {
      const data = await fetchBrandSeries(brand.id);
      setList(data);
    } catch (e) {
      toast.error("Couldn't load series.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand.id]);

  const addSeries = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      await adminCreateSeries(brand.id, { name: newName.trim() });
      setNewName("");
      await load();
      toast.success("Series added.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Add failed.");
    }
  };

  const saveRename = async (id) => {
    if (!editName.trim()) return;
    try {
      await adminUpdateSeries(id, { name: editName.trim() });
      setEditingId(null);
      setEditName("");
      await load();
      toast.success("Renamed.");
    } catch (err) {
      toast.error("Rename failed.");
    }
  };

  const removeSeries = async (id) => {
    if (!window.confirm("Delete this series?")) return;
    await adminDeleteSeries(id);
    await load();
    toast.success("Deleted.");
  };

  const moveSeries = async (idx, delta) => {
    if (!Array.isArray(list)) return;
    const target = idx + delta;
    if (target < 0 || target >= list.length) return;
    const reordered = [...list];
    const [item] = reordered.splice(idx, 1);
    reordered.splice(target, 0, item);
    setList(reordered); // optimistic
    try {
      await adminReorderSeries(brand.id, reordered.map((s) => s.id));
    } catch (e) {
      toast.error("Reorder failed.");
      await load(); // revert on error
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4 overflow-y-auto">
      <div
        className="w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6 my-8"
        data-testid="series-manager"
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-amber-600 font-bold">
              Product series
            </p>
            <h3 className="font-heading text-xl font-semibold text-slate-900">
              {brand.name}
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Name the product ranges this brand offers (e.g. "Myrius", "Penta
              Modular", "Garnet"). Customers will see these as tabs under this
              brand.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={addSeries} className="flex gap-2 mb-5">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New series name (e.g. Myrius)"
            className="flex-1 px-4 py-2 rounded-xl border border-slate-200 focus:border-amber-500 outline-none text-sm"
            data-testid="series-new-name"
          />
          <button
            type="submit"
            className="inline-flex items-center gap-1 px-4 py-2 bg-slate-900 text-white rounded-xl text-sm hover:bg-amber-500 hover:text-slate-900"
            data-testid="series-add"
          >
            <Plus className="w-4 h-4" /> Add
          </button>
        </form>

        {loading ? (
          <p className="text-sm text-slate-400 text-center py-6">Loading…</p>
        ) : list.length === 0 ? (
          <p className="text-sm text-slate-400 italic text-center py-6">
            No series yet — add the brand's product ranges above.
          </p>
        ) : (
          <ul className="space-y-2" data-testid="series-list">
            {list.map((s, sIdx) => (
              <li
                key={s.id}
                className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-xl"
                data-testid={`series-row-${s.id}`}
              >
                {editingId === s.id ? (
                  <>
                    <input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="flex-1 px-3 py-1.5 rounded-lg border border-amber-400 outline-none text-sm"
                      autoFocus
                    />
                    <button
                      onClick={() => saveRename(s.id)}
                      className="px-3 py-1.5 text-xs rounded-full bg-slate-900 text-white hover:bg-amber-500 hover:text-slate-900"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setEditingId(null);
                        setEditName("");
                      }}
                      className="px-3 py-1.5 text-xs rounded-full bg-slate-100 hover:bg-slate-200"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex flex-col -my-1 mr-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => moveSeries(sIdx, -1)}
                        disabled={sIdx === 0}
                        className="p-0.5 text-slate-500 hover:text-amber-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Move up"
                        data-testid={`series-up-${s.id}`}
                      >
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveSeries(sIdx, 1)}
                        disabled={sIdx === list.length - 1}
                        className="p-0.5 text-slate-500 hover:text-amber-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Move down"
                        data-testid={`series-down-${s.id}`}
                      >
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <span className="flex-1 text-sm font-medium text-slate-900 truncate">
                      {s.name}
                      {s.catalog_filename && (
                        <span className="ml-2 text-[11px] text-amber-600 font-normal italic">
                          · {s.catalog_filename}
                        </span>
                      )}
                    </span>
                    {s.catalog_path ? (
                      <>
                        <a
                          href={seriesCatalogViewUrl(s.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 text-slate-500 hover:text-amber-600"
                          title="Open PDF in new tab"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </a>
                        <a
                          href={seriesCatalogUrl(s.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 text-slate-500 hover:text-amber-600"
                          title="Download PDF"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </a>
                      </>
                    ) : null}
                    <label
                      className={`p-1.5 text-slate-500 hover:text-slate-900 cursor-pointer ${typeof seriesUploadProgress[s.id] === "number" ? "opacity-60 pointer-events-none" : ""}`}
                      title={s.catalog_path ? "Replace catalog" : "Upload catalog PDF"}
                    >
                      {typeof seriesUploadProgress[s.id] === "number" ? (
                        <span className="text-[10px] font-mono">
                          {seriesUploadProgress[s.id]}%
                        </span>
                      ) : (
                        <Upload className="w-3.5 h-3.5" />
                      )}
                      <input
                        type="file"
                        accept=".pdf"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          setSeriesUploadProgress((p) => ({ ...p, [s.id]: 0 }));
                          try {
                            await adminUploadSeriesCatalog(s.id, file, (pct) => {
                              setSeriesUploadProgress((p) => ({ ...p, [s.id]: pct }));
                            });
                            await load();
                            toast.success("Catalog uploaded.");
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Upload failed.");
                          } finally {
                            setSeriesUploadProgress((p) => {
                              const n = { ...p };
                              delete n[s.id];
                              return n;
                            });
                          }
                          e.target.value = "";
                        }}
                      />
                    </label>
                    {s.catalog_path && (
                      <button
                        onClick={async () => {
                          await adminDeleteSeriesCatalog(s.id);
                          await load();
                          toast.success("Catalog removed.");
                        }}
                        className="p-1.5 text-slate-400 hover:text-red-600"
                        title="Remove catalog"
                      >
                        <FileText className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => setProductsForSeries(s)}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium rounded-full bg-slate-900 text-white hover:bg-amber-500 hover:text-slate-900"
                      title="Manage products in this series"
                      data-testid={`series-products-${s.id}`}
                    >
                      <Package className="w-3 h-3" /> Products
                    </button>
                    <button
                      onClick={() => {
                        setEditingId(s.id);
                        setEditName(s.name);
                      }}
                      className="p-1.5 text-slate-500 hover:text-slate-900"
                      title="Rename"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => removeSeries(s.id)}
                      className="p-1.5 text-red-500 hover:text-red-700"
                      title="Delete series"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-full bg-slate-100 hover:bg-slate-200"
          >
            Done
          </button>
        </div>
      </div>

      {productsForSeries && (
        <ProductManager
          brand={brand}
          seriesFilter={productsForSeries}
          onClose={() => setProductsForSeries(null)}
        />
      )}
    </div>
  );
}


function ProductManager({ brand, seriesFilter, onClose }) {
  const [products, setProducts] = useState([]);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | product

  const load = async () => {
    try {
      const [p, s] = await Promise.all([
        fetchProducts({ brand_id: brand.id }),
        fetchBrandSeries(brand.id),
      ]);
      const filtered = seriesFilter ? p.filter((x) => x.series_id === seriesFilter.id) : p;
      setProducts(filtered);
      setSeries(s);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand.id]);

  const save = async (payload, imageFiles) => {
    try {
      let product;
      const finalPayload = {
        ...payload,
        // Auto-assign series_id when adding from a specific series context
        series_id: payload.series_id || (seriesFilter ? seriesFilter.id : null),
      };
      if (editing === "new") {
        product = await adminCreateProduct({
          ...finalPayload,
          brand_id: brand.id,
          category_id: brand.category_id,
        });
      } else {
        await adminUpdateProduct(editing.id, finalPayload);
        product = editing;
      }
      if (imageFiles && imageFiles.length > 0) {
        await adminUploadProductImages(product.id, imageFiles);
      }
      await load();
      setEditing(null);
      toast.success("Saved.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed.");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    await adminDeleteProduct(p.id);
    await load();
    toast.success("Deleted.");
  };

  const seriesName = (id) => series.find((s) => s.id === id)?.name;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div
        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl p-6"
        data-testid="product-manager"
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-amber-600 font-bold">
              {seriesFilter ? `${brand.name} · Series` : "Product samples"}
            </p>
            <h3 className="font-heading text-xl font-semibold text-slate-900">
              {seriesFilter ? seriesFilter.name : brand.name}
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              {seriesFilter
                ? `Products in this series will be auto-assigned to "${seriesFilter.name}".`
                : "Add photos of the actual switches / fans / lights you stock. Assign to a series so customers filtering by range see the right items."}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex justify-end mb-4">
          <button
            onClick={() => setEditing("new")}
            className="inline-flex items-center gap-1 px-4 py-2 bg-slate-900 text-white rounded-full text-sm hover:bg-amber-500 hover:text-slate-900"
            data-testid="product-add"
          >
            <Plus className="w-4 h-4" /> Add product
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-slate-400 text-center py-6">Loading…</p>
        ) : products.length === 0 ? (
          <p className="text-sm text-slate-400 italic text-center py-8">
            No products yet — click "Add product" above.
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {products.map((p) => (
              <div
                key={p.id}
                className="bg-slate-50 rounded-xl overflow-hidden border border-slate-200"
                data-testid={`product-row-${p.id}`}
              >
                <div className="aspect-square bg-white overflow-hidden">
                  {p.image_url ? (
                    <img
                      src={productImageSrc(p.image_url)}
                      alt={p.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-slate-300">
                      <ImagePlus className="w-8 h-8" />
                    </div>
                  )}
                </div>
                <div className="p-3">
                  {p.series_id && seriesName(p.series_id) && (
                    <p className="text-[10px] uppercase tracking-[0.15em] text-amber-600 font-bold mb-1">
                      {seriesName(p.series_id)}
                    </p>
                  )}
                  <p className="text-sm font-medium text-slate-900 line-clamp-2 leading-snug">
                    {p.name}
                  </p>
                  <div className="flex items-center gap-1 mt-2">
                    <button
                      onClick={() => setEditing(p)}
                      className="p-1.5 text-slate-500 hover:text-slate-900"
                      title="Edit"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => remove(p)}
                      className="p-1.5 text-red-500 hover:text-red-700"
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-full bg-slate-100 hover:bg-slate-200"
          >
            Done
          </button>
        </div>

        {editing && (
          <ProductEditor
            initial={editing === "new"
              ? (seriesFilter ? { series_id: seriesFilter.id } : null)
              : products.find((p) => p.id === editing.id) || editing}
            series={series}
            onCancel={() => setEditing(null)}
            onSave={save}
            onPhotosChanged={load}
          />
        )}
      </div>
    </div>
  );
}

function ProductEditor({ initial, series, onCancel, onSave, onPhotosChanged }) {
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [seriesId, setSeriesId] = useState(initial?.series_id || "");
  const [files, setFiles] = useState([]);
  const [pendingQueue, setPendingQueue] = useState([]); // raw files awaiting crop
  const [cropFile, setCropFile] = useState(null);       // current file in cropper
  const [saving, setSaving] = useState(false);
  const existingImages = initial?.image_urls || (initial?.image_url ? [initial.image_url] : []);
  const existingImageIds = (initial?.images || []).map((i) => i.id);

  // When a new file enters the queue, start cropping the first
  useEffect(() => {
    if (!cropFile && pendingQueue.length > 0) {
      setCropFile(pendingQueue[0]);
    }
  }, [pendingQueue, cropFile]);

  const acceptCropped = (cropped) => {
    setFiles((prev) => [...prev, cropped]);
    setPendingQueue((q) => q.slice(1));
    setCropFile(null);
  };

  const skipCrop = () => {
    setPendingQueue((q) => q.slice(1));
    setCropFile(null);
  };

  const removeExisting = async (idx) => {
    if (!initial?.id) return;
    const imgId = existingImageIds[idx];
    if (!imgId) return;
    if (!window.confirm("Remove this photo?")) return;
    try {
      await adminDeleteProductImage(initial.id, imgId);
      toast.success("Photo removed.");
      onPhotosChanged && (await onPhotosChanged());
    } catch (e) {
      toast.error("Failed to remove photo.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    await onSave(
      {
        name: name.trim(),
        description: description.trim(),
        series_id: seriesId || null,
        subtype: "",
      },
      files,
    );
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-slate-900/70 backdrop-blur-sm p-4 overflow-y-auto">
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 my-8"
        data-testid="product-editor"
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading text-xl font-semibold text-slate-900">
            {initial ? "Edit Product" : "Add Product"}
          </h3>
          <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-900">
            <X className="w-5 h-5" />
          </button>
        </div>

        <label className="block text-xs uppercase tracking-[0.18em] text-slate-500 font-bold mb-2">
          Product name
        </label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoFocus
          placeholder="e.g. 2-Way 16A Modular Switch"
          className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-amber-500 outline-none mb-4"
          data-testid="product-name"
        />

        <label className="block text-xs uppercase tracking-[0.18em] text-slate-500 font-bold mb-2">
          Series <span className="text-slate-300">(optional)</span>
        </label>
        <select
          value={seriesId}
          onChange={(e) => setSeriesId(e.target.value)}
          className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-amber-500 outline-none mb-4 bg-white"
          data-testid="product-series"
        >
          <option value="">— None —</option>
          {series.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>

        <label className="block text-xs uppercase tracking-[0.18em] text-slate-500 font-bold mb-2">
          Description <span className="text-slate-300">(optional)</span>
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          placeholder="Short description visible on product card"
          className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-amber-500 outline-none mb-4 resize-none"
        />

        {existingImages.length > 0 && (
          <>
            <label className="block text-xs uppercase tracking-[0.18em] text-slate-500 font-bold mb-2">
              Current photos ({existingImages.length})
            </label>
            <div className="grid grid-cols-4 gap-2 mb-4">
              {existingImages.map((url, idx) => (
                <div
                  key={idx}
                  className="relative aspect-square rounded-lg overflow-hidden bg-slate-100 group"
                  data-testid={`existing-photo-${idx}`}
                >
                  <img
                    src={productImageSrc(url)}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                  {existingImageIds[idx] && (
                    <button
                      type="button"
                      onClick={() => removeExisting(idx)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Remove photo"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        <label className="block text-xs uppercase tracking-[0.18em] text-slate-500 font-bold mb-2">
          {existingImages.length > 0 ? "Add more photos" : "Photos"}{" "}
          <span className="text-slate-300">(JPG / PNG / WEBP — select multiple)</span>
        </label>
        <label
          className="flex items-center justify-center gap-2 w-full px-4 py-6 rounded-xl border-2 border-dashed border-slate-300 hover:border-amber-500 cursor-pointer mb-5"
        >
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => {
              const picked = Array.from(e.target.files || []);
              if (picked.length > 0) setPendingQueue((q) => [...q, ...picked]);
              e.target.value = "";
            }}
            className="hidden"
            data-testid="product-image-input"
          />
          {files.length > 0 ? (
            <span className="text-sm text-slate-900 flex items-center gap-2">
              <ImagePlus className="w-4 h-4 text-amber-600" />
              {files.length} photo{files.length === 1 ? "" : "s"} cropped &amp; ready
            </span>
          ) : (
            <span className="text-sm text-slate-500 flex items-center gap-2">
              <ImagePlus className="w-4 h-4" /> Click to upload photos (you can pick multiple)
            </span>
          )}
        </label>

        {cropFile && (
          <ImageCropperModal
            file={cropFile}
            aspect={1}
            presetLabel="1:1 · Product photo"
            onCancel={skipCrop}
            onConfirm={acceptCropped}
          />
        )}

        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-sm rounded-full bg-slate-100 hover:bg-slate-200">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-5 py-2 text-sm rounded-full bg-slate-900 text-white hover:bg-amber-500 hover:text-slate-900 disabled:opacity-50"
            data-testid="product-save"
          >
            {saving ? "Saving…" : initial ? "Save changes" : "Add product"}
          </button>
        </div>
      </form>
    </div>
  );
}


function CategoryImagesPanel({ categories, onChange }) {
  const [busyId, setBusyId] = useState(null);
  const [cropPayload, setCropPayload] = useState(null);

  const upload = async (cat, file) => {
    if (!file) return;
    setBusyId(cat.id);
    try {
      await adminUploadCategoryImage(cat.id, file);
      await onChange();
      toast.success(`${cat.name} image updated.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed.");
    } finally {
      setBusyId(null);
    }
  };

  const reset = async (cat) => {
    if (!window.confirm(`Reset ${cat.name} image to default?`)) return;
    setBusyId(cat.id);
    try {
      await adminResetCategoryImage(cat.id);
      await onChange();
      toast.success("Reset.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="px-4 sm:px-6 py-4 border-b border-slate-100">
        <h3 className="font-heading text-lg font-semibold text-slate-900">
          Category banner images
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          These images appear at the top of each category page.
        </p>
      </div>
      <ul className="divide-y divide-slate-100">
        {categories.map((c) => (
          <li
            key={c.id}
            className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 px-4 sm:px-6 py-4"
            data-testid={`cat-image-row-${c.id}`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-20 h-14 sm:w-28 sm:h-20 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0">
                <img
                  src={productImageSrc(c.image)}
                  alt={c.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-heading font-semibold text-slate-900 truncate">{c.name}</p>
                <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{c.tagline}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:flex-shrink-0 sm:ml-auto">
              <label className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1 px-4 py-2 text-xs rounded-full bg-amber-500 text-slate-900 hover:bg-amber-400 cursor-pointer">
                <Upload className="w-3.5 h-3.5" />
                {busyId === c.id ? "Uploading…" : "Replace"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={busyId === c.id}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) setCropPayload({ cat: c, file: f });
                    e.target.value = "";
                  }}
                />
              </label>
              {c.image && c.image.includes("/api/files/category-image/") && (
                <button
                  onClick={() => reset(c)}
                  className="inline-flex items-center gap-1 px-3 py-2 text-xs rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200"
                  title="Reset to default"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>

      {cropPayload && (
        <ImageCropperModal
          file={cropPayload.file}
          aspect={16 / 9}
          presetLabel="16:9 · Category banner"
          onCancel={() => setCropPayload(null)}
          onConfirm={async (cropped) => {
            const cat = cropPayload.cat;
            setCropPayload(null);
            await upload(cat, cropped);
          }}
        />
      )}
    </div>
  );
}

function ShowroomPhotosPanel() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [pendingQueue, setPendingQueue] = useState([]);
  const [cropFile, setCropFile] = useState(null);
  const [accumulated, setAccumulated] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchShowroomPhotos();
      setPhotos(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Pull next file off queue when cropper closes
  useEffect(() => {
    if (!cropFile && pendingQueue.length > 0) {
      setCropFile(pendingQueue[0]);
    }
  }, [pendingQueue, cropFile]);

  // When queue empty AND we have accumulated cropped files → upload them all
  useEffect(() => {
    if (!cropFile && pendingQueue.length === 0 && accumulated.length > 0) {
      const toUpload = accumulated;
      setAccumulated([]);
      doUpload(toUpload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cropFile, pendingQueue.length, accumulated.length]);

  const doUpload = async (filesToUpload) => {
    setUploading(true);
    try {
      const result = await adminUploadShowroomPhotos(filesToUpload);
      toast.success(`${result.added.length} photo${result.added.length === 1 ? "" : "s"} uploaded.`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await adminDeleteShowroomPhoto(p.id);
      toast.success("Photo deleted.");
      await load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="admin-showroom-panel">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900">
            Showroom photos
          </h3>
          <p className="text-sm text-slate-500 mt-1 max-w-xl">
            Upload photos of your physical showroom — displays, installed
            products, brand walls, etc. They appear on the About page in a
            gallery. Select multiple files at once for bulk upload.
          </p>
        </div>
        <label
          className={`inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white rounded-full text-sm font-medium cursor-pointer hover:bg-amber-500 hover:text-slate-900 ${
            uploading ? "opacity-60 pointer-events-none" : ""
          }`}
          data-testid="admin-showroom-upload"
        >
          <Upload className="w-4 h-4" />
          {uploading ? "Uploading…" : "Upload photos"}
          <input
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              const picked = Array.from(e.target.files || []);
              if (picked.length > 0) setPendingQueue((q) => [...q, ...picked]);
              e.target.value = "";
            }}
          />
        </label>
      </div>

      {cropFile && (
        <ImageCropperModal
          file={cropFile}
          aspect={null}
          presetLabel="Free crop · Showroom"
          onCancel={() => {
            setPendingQueue((q) => q.slice(1));
            setCropFile(null);
          }}
          onConfirm={(cropped) => {
            setAccumulated((arr) => [...arr, cropped]);
            setPendingQueue((q) => q.slice(1));
            setCropFile(null);
          }}
        />
      )}

      {loading ? (
        <p className="text-sm text-slate-400 text-center py-12">Loading…</p>
      ) : photos.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 p-12 text-center">
          <ImagePlus className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">
            No showroom photos yet. Click <strong>Upload photos</strong> to add some.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {photos.map((p) => (
            <div
              key={p.id}
              className="relative aspect-square rounded-xl overflow-hidden bg-slate-50 border border-slate-200 group"
              data-testid={`admin-showroom-photo-${p.id}`}
            >
              <img
                src={productImageSrc(p.image_url)}
                alt={p.caption || "Showroom"}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <button
                type="button"
                onClick={() => remove(p)}
                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                title="Delete photo"
                data-testid={`admin-showroom-delete-${p.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
